import { useMemo } from "react";
import styled from "styled-components";
import { Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { BreadcrumbsCommon, StyledFavoritesPage } from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_HOME, PATH_PROFILE } from "@routes";
import { breakpoint } from "@utilities";
import { FavoritesContent } from "src/Pages/Favorites";
import { ProfileDetailsTab } from "./ProfileDetailsTab";
import { ProfileAddressBookTab } from "./ProfileAddressBookTab";

const TAB_KEYS = ["details", "favourites", "address-book"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const normaliseTab = (value: string | null): TabKey =>
    TAB_KEYS.includes(value as TabKey) ? (value as TabKey) : "details";

export const Profile = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = useMemo(() => normaliseTab(searchParams.get("tab")), [searchParams]);

    const breadcrumbsData = [
        { title: "Home", link: PATH_HOME },
        { title: "Profile", link: PATH_PROFILE },
    ];

    const handleChange = (_e: React.SyntheticEvent, value: TabKey) => {
        setSearchParams({ tab: value });
    };

    return (
        <AppModuleLayout>
            <StyledProfilePage>
                <div className="wrapper">
                    <div className="breadCrumbs">
                        <BreadcrumbsCommon data={breadcrumbsData} />
                    </div>
                    <div className="content">
                        <p className="heading1">Profile</p>
                        <Tabs
                            value={activeTab}
                            onChange={handleChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            className="profileTabs"
                        >
                            <Tab label="My Details" value="details" />
                            <Tab label="Favourites" value="favourites" />
                            <Tab label="Address Book" value="address-book" />
                        </Tabs>

                        <div className="tabPanel">
                            {activeTab === "details" && <ProfileDetailsTab />}
                            {activeTab === "favourites" && (
                                <StyledFavoritesPage>
                                    <FavoritesContent showHeading={false} />
                                </StyledFavoritesPage>
                            )}
                            {activeTab === "address-book" && <ProfileAddressBookTab />}
                        </div>
                    </div>
                </div>
            </StyledProfilePage>
        </AppModuleLayout>
    );
};

const StyledProfilePage = styled.div`
    background-color: #f2f2f2;
    min-height: 60vh;

    ${breakpoint.breakMobile`
        padding: 24px 20px;
        margin-bottom: 90px;
    `}

    .wrapper {
        display: block;
        margin: 0 auto;
        position: relative;

        ${breakpoint.breakTablet`
            width: ${(p) => p.theme.widths.wide_700};
            margin-top: 81px;
            padding-bottom: 20px;
        `}

        ${breakpoint.breakIpadPro`
            width: ${(p) => p.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(p) => p.theme.widths.wide_1110};
        `}

        .breadCrumbs {
            position: absolute;
            bottom: calc(100% + 30px);

            ${breakpoint.breakOnlyMobile`
                position: relative;
            `}
        }
    }

    .content {
        position: relative;
        top: 11px;
        margin-bottom: 11px;

        .heading1 {
            font-size: 28px;
            font-weight: 700;
            line-height: 36px;
            margin-bottom: 16px;
            display: none;

            ${breakpoint.breakTablet`
                display: block;
            `}
        }
    }

    .profileTabs {
        background-color: #fff;
        border-radius: 4px 4px 0 0;
        border-bottom: 1px solid #e6e9eb;
    }

    .tabPanel {
        margin-top: 16px;
    }
`;
